import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createSalesCustomer,
  fetchSalesCustomer,
  fetchSalesCustomers,
  updateSalesCustomer,
  type SalesCustomer,
  type SalesCustomerDetail
} from "../../lib/sales-customers";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type EditorState = {
  id: number | null;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  profilePicture: string;
};

const emptyEditorState: EditorState = {
  id: null,
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  profilePicture: ""
};

function toEditorState(customer: SalesCustomerDetail): EditorState {
  return {
    id: customer.id,
    firstName: customer.firstName,
    middleName: customer.middleName || "",
    lastName: customer.lastName,
    email: customer.email,
    phone: customer.phone,
    address: customer.address,
    profilePicture: customer.profilePicture || ""
  };
}

function customerInitials(customer: Pick<SalesCustomer, "firstName" | "lastName" | "name">) {
  const source = `${customer.firstName} ${customer.lastName}`.trim() || customer.name || "C";
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function statusTone(balance: number) {
  if (balance <= 0) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
  }
  return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
}

export function SalesCustomersPage() {
  const [customers, setCustomers] = useState<SalesCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<SalesCustomerDetail | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalCustomers: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalCustomers: 0,
    customersWithOrders: 0,
    activeThisMonth: 0,
    paidRevenueTotal: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const metrics = useMemo(
    () => [
      { label: "Total customers", value: summary.totalCustomers },
      { label: "Customers with orders", value: summary.customersWithOrders },
      { label: "Active this month", value: summary.activeThisMonth },
      { label: "Paid revenue", value: formatCurrency(summary.paidRevenueTotal) }
    ],
    [summary]
  );

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchSalesCustomers(params);
        if (!("pagination" in result) || !("summary" in result)) {
          throw new Error("Unexpected customer response.");
        }

        if (!ignore) {
          setCustomers(result.customers);
          setPagination(result.pagination);
          setSummary(result.summary);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load customers.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [page, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchSalesCustomers(params);
    if (!("pagination" in result) || !("summary" in result)) {
      throw new Error("Unexpected customer response.");
    }
    setCustomers(result.customers);
    setPagination(result.pagination);
    setSummary(result.summary);
  }

  function openCreate() {
    setSelectedCustomer(null);
    setEditor(emptyEditorState);
    setEditorOpen(true);
    setLoadingEditor(false);
    setError(null);
    setSuccessMessage(null);
  }

  async function openCustomer(customer: SalesCustomer) {
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const detail = await fetchSalesCustomer(customer.id);
      setSelectedCustomer(detail);
      setEditor(toEditorState(detail));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load customer.");
    } finally {
      setLoadingEditor(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.firstName.trim()) {
      setError("First name is required.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (editor.id) {
        await updateSalesCustomer(editor.id, {
          firstName: editor.firstName,
          middleName: editor.middleName,
          lastName: editor.lastName,
          email: editor.email,
          phone: editor.phone,
          address: editor.address,
          profilePicture: editor.profilePicture
        });
        const refreshed = await fetchSalesCustomer(editor.id);
        setSelectedCustomer(refreshed);
        setEditor(toEditorState(refreshed));
        setSuccessMessage("Customer details updated.");
      } else {
        const fullName = [editor.firstName, editor.middleName, editor.lastName].filter(Boolean).join(" ").trim();
        const created = await createSalesCustomer({
          name: fullName,
          email: editor.email,
          phone: editor.phone,
          address: editor.address
        });
        await reloadCurrentView();
        const detail = await fetchSalesCustomer(created.customer.id);
        setSelectedCustomer(detail);
        setEditor(toEditorState(detail));
        setSuccessMessage("Customer created successfully.");
      }

      await reloadCurrentView();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save customer.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales customers</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">Customer desk</h1>
          <p className="mt-4 max-w-3xl text-nv-slate">
            Track customers, review recent buying activity, and keep contact details clean for follow-up and walk-in sales.
          </p>
        </div>
        <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" onClick={openCreate} type="button">
          New customer
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <div className="admin-card rounded-[28px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(360px,1fr)]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="admin-card rounded-[28px] p-5">
            <input
              className="field-control w-full px-4 py-3 text-sm"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by customer name, email, phone, address, or ID"
              value={query}
            />
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}
          {successMessage ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">{successMessage}</div> : null}

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">Customer</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Orders</th>
                    <th className="px-5 py-4">Paid value</th>
                    <th className="px-5 py-4">Last order</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={5}>Loading customers...</td></tr>
                  ) : customers.length > 0 ? (
                    customers.map((customer) => (
                      <tr className="cursor-pointer border-b border-black/5 align-top transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]" key={customer.id} onClick={() => void openCustomer(customer)}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            {customer.profilePicture ? (
                              <img alt={customer.name} className="h-11 w-11 rounded-full object-cover" src={customer.profilePicture} />
                            ) : (
                              <div className="grid h-11 w-11 place-items-center rounded-full bg-cyan-50 text-sm font-semibold text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300">
                                {customerInitials(customer)}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-nv-ink">{customer.name}</p>
                              <p className="mt-1 text-xs text-nv-slate">Customer #{customer.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p>{customer.phone || "No phone"}</p>
                          <p className="mt-1">{customer.email || "No email"}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p className="font-semibold text-nv-ink">{customer.orderCount || 0}</p>
                          <p className="mt-1">{customer.paidOrderCount || 0} paid</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p className="font-semibold text-nv-ink">{formatCurrency(customer.lifetimeRevenue || 0)}</p>
                          <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(customer.outstandingBalance || 0)}`}>
                            Balance {formatCurrency(customer.outstandingBalance || 0)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p>{customer.lastOrderOn ? formatDateTime(customer.lastOrderOn) : "No orders yet"}</p>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={5}>No customers matched the current view.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
              <p>Page {pagination.page} of {pagination.totalPages} · {pagination.totalCustomers} customers</p>
              <div className="flex items-center gap-2">
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasPreviousPage || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasNextPage || loading} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto pr-1">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{editor.id ? "Customer detail" : "New customer"}</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">{editor.id ? selectedCustomer?.name || `Customer #${editor.id}` : "Create customer"}</h2>
              </div>
              {editorOpen ? (
                <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                  Close
                </button>
              ) : null}
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select a customer to review their history, or create a new customer record.
              </div>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Loading customer detail...
              </div>
            ) : (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                {selectedCustomer ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="field-soft p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Lifetime paid</p>
                      <p className="mt-2 text-2xl font-semibold text-nv-ink">{formatCurrency(selectedCustomer.lifetimeRevenue || 0)}</p>
                    </div>
                    <div className="field-soft p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Outstanding</p>
                      <p className="mt-2 text-2xl font-semibold text-nv-ink">{formatCurrency(selectedCustomer.outstandingBalance || 0)}</p>
                    </div>
                    <div className="field-soft p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Orders</p>
                      <p className="mt-2 text-2xl font-semibold text-nv-ink">{selectedCustomer.orderCount || 0}</p>
                    </div>
                    <div className="field-soft p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Last order</p>
                      <p className="mt-2 text-sm font-semibold text-nv-ink">{selectedCustomer.lastOrderOn ? formatDateTime(selectedCustomer.lastOrderOn) : "No orders yet"}</p>
                    </div>
                  </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">First name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, firstName: event.target.value }))} value={editor.firstName} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Last name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, lastName: event.target.value }))} value={editor.lastName} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Middle name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, middleName: event.target.value }))} value={editor.middleName} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Phone</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, phone: event.target.value }))} value={editor.phone} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Email</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, email: event.target.value }))} value={editor.email} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Profile image</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, profilePicture: event.target.value }))} value={editor.profilePicture} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Address</label>
                    <textarea className="field-control min-h-[120px] px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, address: event.target.value }))} value={editor.address} />
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving} type="submit">
                    {saving ? "Saving..." : editor.id ? "Save customer" : "Create customer"}
                  </button>
                  {selectedCustomer ? (
                    <Link className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold text-nv-ink dark:border-white/10" to={`/sales/orders?q=${selectedCustomer.id}`}>
                      Search orders
                    </Link>
                  ) : null}
                </div>

                {selectedCustomer ? (
                  <div className="space-y-3 border-t border-black/10 pt-5 dark:border-white/10">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Recent orders</p>
                      <Link className="text-sm font-semibold text-cyan-600 dark:text-cyan-300" to="/sales/orders">
                        Open order queue
                      </Link>
                    </div>
                    {selectedCustomer.recentOrders.length > 0 ? (
                      selectedCustomer.recentOrders.map((order) => (
                        <div className="field-soft rounded-[24px] p-4" key={order.id}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-nv-ink">Order #{order.id}</p>
                              <p className="mt-1 text-sm text-nv-slate">{order.addedOn ? formatDateTime(order.addedOn) : "Unknown date"}</p>
                            </div>
                            <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(order.balance)}`}>
                              Balance {formatCurrency(order.balance)}
                            </span>
                          </div>
                          <div className="mt-3 grid gap-3 text-sm text-nv-slate sm:grid-cols-2">
                            <p>Total {formatCurrency(order.total)}</p>
                            <p>Paid {formatCurrency(order.amountPaid)}</p>
                            <p>Status {order.status}</p>
                            <p>{order.paymentType || "Payment type not recorded"}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                        No orders linked to this customer yet.
                      </div>
                    )}
                  </div>
                ) : null}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
